import {
  IAIWorkspace,
  IAIWorkspaceRequest,
  useCreateDraftVetRequest,
} from "@entities/aiWorkspace";
import { Collapse } from "antd";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight, Clock, Loader2 } from "lucide-react";
import { useState } from "react";
import { useLocalRequestPrompt } from "../useLocalRequestPrompt";
import { WorkspacePromptEditor } from "../WorkspacePromptEditor";
import { aiWorkspaceRequestId } from "../useAiWorkspaceModuleParams";
import pageStyles from "../styles.module.scss";
import {
  DRAFT_VET_STEPS,
  ModuleProgress,
  vetListStep,
} from "../ModuleProgress";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import { assetsFromDraft } from "./DraftAssetPreview";
import { DraftUploadModal } from "./DraftUploadModal";
import styles from "./styles.module.scss";

function firstLine(value: string) {
  return (
    promptToText(value)
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.length > 0) || ""
  );
}

function assetCountLabel(count: number) {
  return count === 1 ? "1 media asset" : `${count} media assets`;
}

type DraftVetListProps = {
  workspace: IAIWorkspace;
  requests: IAIWorkspaceRequest[];
  onOpenRequest: (requestId: string, type: string) => void;
};

export function DraftVetList({
  workspace,
  requests,
  onOpenRequest,
}: DraftVetListProps) {
  const { promptHtml, initialHtml, updatePrompt, contentKey } =
    useLocalRequestPrompt(
      workspace._id,
      "draft-vet",
      workspace.draftVetMasterPrompt?.prompt || ""
    );
  const [uploadOpen, setUploadOpen] = useState(false);
  const { create, isCreating } = useCreateDraftVetRequest((request) => {
    setUploadOpen(false);
    const id = aiWorkspaceRequestId(request);
    if (id) onOpenRequest(id, "draft-vet");
  });
  const draftRequests = requests.filter((request) => request.type === "draft-vet");

  return (
    <div className={styles.tab}>
      <ModuleProgress
        kicker="Review"
        title="Draft Vet"
        subtitle="Check a draft video or image against the rubric and the approved script."
        steps={DRAFT_VET_STEPS}
        current={vetListStep(promptHtml, draftRequests.length)}
      />
      <WorkspacePromptEditor
        moduleId="draft-vet"
        aiWorkspaceId={workspace._id}
        label="Request prompt"
        hint="Kept on this page until you upload a draft."
        savedPrompt={initialHtml}
        contentKey={contentKey}
        isSaving={false}
        requestOnly
        onHtmlChange={updatePrompt}
      />
      <DraftUploadModal
        open={uploadOpen}
        submitting={isCreating}
        onClose={() => setUploadOpen(false)}
        onSubmit={(mediaAssets) =>
          create({
            aiWorkspaceId: workspace._id,
            masterPrompt: promptHtml,
            mediaAssets,
          })
        }
      />
      <Collapse
        className={pageStyles.requestsCollapse}
        defaultActiveKey={[]}
        items={[
          {
            key: "requests",
            label: (
              <div className={pageStyles.collapseLabel}>
                <span className={pageStyles.collapseTitle}>Requests</span>
                <span className={pageStyles.collapseCount}>
                  {draftRequests.length}
                </span>
              </div>
            ),
            extra: (
              <button
                type="button"
                className={pageStyles.collapseActionBtn}
                disabled={isCreating}
                onClick={(e) => {
                  e.stopPropagation();
                  setUploadOpen(true);
                }}
              >
                {isCreating ? (
                  <Loader2 size={13} className="spin-animation" />
                ) : null}
                New draft check
              </button>
            ),
            children: (
              draftRequests.length === 0 ? (
                <div className={pageStyles.emptyRequests}>
                  <p className={pageStyles.emptyTitle}>No drafts yet</p>
                  <p className={pageStyles.emptySub}>
                    Upload a draft to start a new check.
                  </p>
                </div>
              ) : (
                <div className={pageStyles.requestsList}>
                  {draftRequests.map((request) => {
                    const count = assetsFromDraft(request.drafting).length;
                    const prompt = firstLine(request.masterPrompt || "");
                    return (
                      <button
                        key={request._id || request.id}
                        type="button"
                        className={pageStyles.requestItem}
                        onClick={() =>
                          onOpenRequest(aiWorkspaceRequestId(request), "draft-vet")
                        }
                      >
                        <div className={pageStyles.requestInfo}>
                          <div className={pageStyles.requestTags}>
                            <span className={pageStyles.timeTag}>
                              <Clock size={11} />
                              {request.updatedAt
                                ? formatDistanceToNow(new Date(request.updatedAt), {
                                    addSuffix: true,
                                  })
                                : "-"}
                            </span>
                          </div>
                          <p className={pageStyles.requestTitle}>{assetCountLabel(count)}</p>
                          {prompt ? (
                            <p className={pageStyles.requestSubtitle}>{prompt}</p>
                          ) : null}
                        </div>
                        <ArrowRight size={15} className={pageStyles.requestArrow} />
                      </button>
                    );
                  })}
                </div>
              )
            ),
          },
        ]}
      />
    </div>
  );
}
